import { db } from "../../services/db";
import { callEmbedding, type EmbeddingRoute } from "../../services/llm";
import { dedupeDocuments, rankVectorRows, renderRagDocuments } from "./logic";
import type { GlobalVectorMatch, RagDocument, RagSearchOptions } from "./types";
interface MonsterIndex {
  records: Array<{
    id: string;
    name: string;
    name_en: string;
    weaknesses: string[];
    elements: string[];
    habitats: string[];
    summary: string;
  }>;
}
let catalogCache: MonsterIndex | null = null;
async function catalog() {
  if (catalogCache) return catalogCache;
  const loaded = await fetch("/data/monsters-index.json").then(
    (response) => response.json() as Promise<MonsterIndex>,
  );
  catalogCache = loaded;
  return loaded;
}
function lexicalScore(query: string, text: string) {
  const normalized = query.toLowerCase(),
    tokens = [
      ...new Set(
        normalized
          .split(/[\s，。！？、；：,.!?;:'"“”‘’（）()\[\]【】/]+/)
          .filter((token) => token.length > 1),
      ),
    ];
  let score = 0;
  for (const token of tokens)
    if (text.toLowerCase().includes(token))
      score += Math.min(4, 1 + token.length / 4);
  return score;
}
async function searchCatalog(
  query: string,
  limit: number,
): Promise<RagDocument[]> {
  const { records } = await catalog();
  return records
    .map((monster) => {
      const content = `${monster.name}（${monster.name_en}）：弱点 ${monster.weaknesses.join("/") || "资料未定"}；属性 ${monster.elements.join("/") || "无明确记录"}；栖息地 ${monster.habitats.join("、") || "资料未定"}；${monster.summary}`;
      return {
        id: `monster:${monster.id}`,
        content,
        score: lexicalScore(
          query,
          `${monster.name} ${monster.name_en} ${content}`,
        ),
        metadata: { kind: "monster", monsterId: monster.id },
        source: "catalog" as const,
      };
    })
    .filter((document) => document.score > 0)
    .sort((a, b) => b.score - a.score)
    .slice(0, limit);
}
async function searchGlobal(
  vector: number[],
  topK: number,
  signal?: AbortSignal,
): Promise<RagDocument[]> {
  const response = await fetch("/api/global-vector-search", {
    method: "POST",
    headers: { "Content-Type": "application/json" },
    body: JSON.stringify({ vector, index: "lore", topK }),
    signal,
  });
  if (!response.ok) throw new Error(`公共知识库查询失败 (${response.status})`);
  const payload = (await response.json()) as { matches?: GlobalVectorMatch[] };
  return (payload.matches ?? []).flatMap((match) => {
    const content =
      typeof match.metadata?.content === "string" ? match.metadata.content : "";
    return content
      ? [
          {
            id: match.id,
            content,
            score: match.score,
            metadata: match.metadata ?? {},
            source: "global" as const,
          },
        ]
      : [];
  });
}
export async function searchRag(
  query: string,
  embedding: EmbeddingRoute | undefined,
  options: Partial<RagSearchOptions> = {},
  signal?: AbortSignal,
) {
  const config: RagSearchOptions = {
    topK: 8,
    minScore: 0.28,
    includeArchive: true,
    includeGlobal: false,
    includeCatalogFallback: true,
    ...options,
  };
  const documents: RagDocument[] = [];
  if (embedding) {
    const [vector] = await callEmbedding(embedding, query, signal);
    if (config.includeArchive) {
      const rows = await db.vectors.where("scope").equals("archive").toArray();
      documents.push(...rankVectorRows(rows, vector, config.minScore));
    }
    if (config.includeGlobal)
      try {
        documents.push(...(await searchGlobal(vector, config.topK, signal)));
      } catch {
        /* public Vectorize is intentionally fail-open while corpus work is paused */
      }
  }
  if (config.includeCatalogFallback)
    documents.push(...(await searchCatalog(query, config.topK)));
  return dedupeDocuments(documents, config.topK);
}
export async function gatherRagContext(
  query: string,
  embedding?: EmbeddingRoute,
  signal?: AbortSignal,
) {
  return renderRagDocuments(
    await searchRag(query, embedding, { includeGlobal: false }, signal),
  );
}
export async function indexArchiveText(
  archiveName: string,
  content: string,
  embedding: EmbeddingRoute,
  metadata: Record<string, unknown> = {},
  signal?: AbortSignal,
) {
  const [vector] = await callEmbedding(embedding, content, signal);
  const createdAt = Date.now(),
    id = `archive:${archiveName}:${createdAt}`;
  await db.vectors.put({
    id,
    scope: "archive",
    archiveName,
    content,
    vector,
    metadata: { ...metadata, archiveName },
    createdAt,
  });
  return id;
}
